import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import { useAuth } from "../context/AuthContext";
import { getAppointmentStatusReport } from "../api/reports";
import { listStaff } from "../api/staffs";
import { getCurrentSalonId } from "../utils/currentSalon";
import { getSalonTimezone } from "../utils/salonTimezone";
import { formatInTimeZone } from "../utils/datetime";
import { BTN_PRIMARY, FIELD_CLASS, LABEL_TEXT, MODAL_TEXT } from "../ui/modalStyles";
import { REPORT_HELP } from "../constants/reportHelpContent";
import "./ReportsSectionCompact.css";

const STATUS_OPTIONS = [
  { value: "scheduled", label: "Scheduled" },
  { value: "checked_in", label: "Checked-in" },
  { value: "confirmed", label: "Confirmed" },
  { value: "closed", label: "Closed" },
];

const GROUP_BY_OPTIONS = [
  { value: "status", label: "Status" },
  { value: "employee", label: "Employee" },
  { value: "day_of_week", label: "Day of week" },
  { value: "time_of_day", label: "Time of day" },
];

const BUCKET_OPTIONS = [15, 30, 60];

const formatDateISO = (value, timeZone) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(value);
    } catch (err) {
      // Fall back to local time when the timezone is invalid.
    }
  }
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatStatusLabel = (value) => {
  const match = STATUS_OPTIONS.find((opt) => opt.value === value);
  if (match) return match.label;
  const text = String(value || "").replace(/_/g, " ").trim();
  if (!text) return "";
  return text
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export default function AppointmentStatusReport() {
  const navigate = useNavigate();
  const { token, user, currentSalon, salonTimezone: authSalonTimezone } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.APPOINTMENT_STATUS;
  const salonId = getCurrentSalonId(user);
  const baseTimezone = useMemo(
    () => getSalonTimezone(currentSalon) || authSalonTimezone || "UTC",
    [currentSalon, authSalonTimezone]
  );
  const today = useMemo(() => formatDateISO(new Date(), baseTimezone), [baseTimezone]);

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [groupBy, setGroupBy] = useState("status");
  const [bucketMinutes, setBucketMinutes] = useState(60);
  const [statuses, setStatuses] = useState(STATUS_OPTIONS.map((opt) => opt.value));
  const [employeeId, setEmployeeId] = useState("");
  const [staff, setStaff] = useState([]);
  const [staffError, setStaffError] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [reportData, setReportData] = useState(null);
  const [reportError, setReportError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setStartDate(today);
    setEndDate(today);
  }, [today]);

  useEffect(() => {
    if (!salonId || !token) return;
    let mounted = true;

    const loadStaff = async () => {
      try {
        setStaffError("");
        const data = await listStaff({ salon_id: salonId }, token);
        if (mounted) {
          setStaff(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (mounted) {
          setStaff([]);
          setStaffError("Unable to load staff list.");
        }
      }
    };

    loadStaff();
    return () => {
      mounted = false;
    };
  }, [salonId, token]);

  const toggleStatus = (value) => {
    setStatuses((prev) => {
      if (prev.includes(value)) {
        return prev.filter((status) => status !== value);
      }
      return [...prev, value];
    });
  };

  const isInvalidDateRange = useMemo(() => {
    return startDate && endDate && startDate > endDate;
  }, [startDate, endDate]);

  const isTimePairIncomplete = useMemo(() => {
    return (startTime && !endTime) || (!startTime && endTime);
  }, [startTime, endTime]);

  const isInvalidTimeRange = useMemo(() => {
    return startTime && endTime && startTime > endTime;
  }, [startTime, endTime]);

  const handleRunReport = async () => {
    if (isInvalidDateRange) {
      setReportError("End date can't be before start date.");
      return;
    }
    if (isTimePairIncomplete) {
      setReportError("Start time and end time are required.");
      return;
    }
    if (isInvalidTimeRange) {
      setReportError("Start time must be earlier than end time.");
      return;
    }
    if (!statuses.length) {
      setReportError("Select at least one status.");
      return;
    }
    setReportError("");
    setLoading(true);
    try {
      const payload = {
        start_date: startDate,
        end_date: endDate,
        statuses,
        group_by: groupBy,
        sort_dir: "asc",
        bucket_minutes: bucketMinutes,
        employee_id: employeeId ? Number(employeeId) : null,
      };
      if (startTime && endTime) {
        payload.start_time_local = startTime;
        payload.end_time_local = endTime;
      }
      const data = await getAppointmentStatusReport(payload);
      setReportData(data);
    } catch (err) {
      setReportError("Couldn't load appointments. Please try again.");
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const activeTimezone = reportData?.meta?.timezone || baseTimezone;

  const handleRowClick = (appointment) => {
    if (!appointment?.appointment_id) return;
    const dateStr = formatInTimeZone(
      appointment.start_at,
      activeTimezone,
      { year: "numeric", month: "2-digit", day: "2-digit" },
      "en-CA"
    );
    const params = new URLSearchParams();
    if (dateStr) params.set("date", dateStr);
    params.set("appointmentId", String(appointment.appointment_id));
    if (appointment.employee_id) {
      params.set("employeeId", String(appointment.employee_id));
    }
    navigate(`/manager/schedule?${params.toString()}`);
  };

  const groups = reportData?.groups || [];
  const reportTimezone = activeTimezone;
  const statusTotals = useMemo(() => {
    const totals = {};
    STATUS_OPTIONS.forEach((status) => {
      totals[status.value] = 0;
    });
    groups.forEach((group) => {
      STATUS_OPTIONS.forEach((status) => {
        totals[status.value] += Number(group.counts?.[status.value] || 0);
      });
    });
    return totals;
  }, [groups]);

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader
            title="Appointment Status Report"
            closeTo="/manager/reports"
            closeLabel="Close report"
          />
          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div
              id="appointment-status-overview"
              className={getSectionClassName("appointment-status-overview")}
            >
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                storageKey="report_help_appointment_status"
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            <div
              id="appointment-status-filters"
              className={`rounded-2xl border border-slate-200 bg-slate-50 p-4 md:p-6 ${getSectionClassName(
                "appointment-status-filters"
              )}`}
            >
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex flex-col gap-1">
                  <label className={LABEL_TEXT}>Date From</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className={FIELD_CLASS}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className={LABEL_TEXT}>Date To</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className={FIELD_CLASS}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className={LABEL_TEXT}>Start Time</label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(event) => setStartTime(event.target.value)}
                    className={FIELD_CLASS}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className={LABEL_TEXT}>End Time</label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(event) => setEndTime(event.target.value)}
                    className={FIELD_CLASS}
                  />
                  {isTimePairIncomplete || isInvalidTimeRange ? (
                    <div className="text-sm text-rose-600">
                      {isTimePairIncomplete
                        ? "Start time and end time are required."
                        : "Start time must be earlier than end time."}
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-col gap-1 min-w-[180px]">
                  <label className={LABEL_TEXT}>Group By</label>
                  <select
                    value={groupBy}
                    onChange={(event) => setGroupBy(event.target.value)}
                    className={FIELD_CLASS}
                  >
                    {GROUP_BY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1 min-w-[140px]">
                  <label className={LABEL_TEXT}>Bucket</label>
                  <select
                    value={bucketMinutes}
                    onChange={(event) => setBucketMinutes(Number(event.target.value))}
                    className={FIELD_CLASS}
                    disabled={groupBy !== "time_of_day"}
                  >
                    {BUCKET_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option} min
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1 min-w-[200px]">
                  <label className={LABEL_TEXT}>Employee</label>
                  <select
                    value={employeeId}
                    onChange={(event) => setEmployeeId(event.target.value)}
                    className={FIELD_CLASS}
                  >
                    <option value="">All employees</option>
                    {staff.map((member) => (
                      <option key={member.id} value={String(member.id)}>
                        {member.display_name || member.full_name || `Staff ${member.id}`}
                      </option>
                    ))}
                  </select>
                  {staffError ? (
                    <div className="text-sm text-rose-600">{staffError}</div>
                  ) : null}
                </div>
                <div className="flex flex-col gap-1 min-w-[260px]">
                  <label className={LABEL_TEXT}>Status</label>
                  <div className="flex flex-wrap gap-2">
                    {STATUS_OPTIONS.map((status) => (
                      <label
                        key={status.value}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={statuses.includes(status.value)}
                          onChange={() => toggleStatus(status.value)}
                        />
                        {status.label}
                      </label>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRunReport}
                  className={BTN_PRIMARY}
                  disabled={loading}
                >
                  {loading ? "Running..." : "Run Report"}
                </button>
              </div>
            </div>

            {reportError ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {reportError}
              </div>
            ) : null}

            {reportData ? (
              <div className="space-y-4">
                <div className="text-sm text-slate-600">
                  Timezone: {reportTimezone}
                </div>
                <div
                  id="appointment-status-kpis"
                  className={`grid grid-cols-1 md:grid-cols-3 gap-3 ${getSectionClassName(
                    "appointment-status-kpis"
                  )}`}
                >
                  {STATUS_OPTIONS.map((status) => (
                    <div
                      key={`status-total-${status.value}`}
                      className="rounded-2xl border border-slate-200 bg-white p-4"
                    >
                      <div className="text-xs uppercase text-slate-500">
                        {status.label}
                      </div>
                      <div className="mt-2 text-lg font-semibold text-slate-900">
                        {statusTotals[status.value] ?? 0}
                      </div>
                    </div>
                  ))}
                </div>
                {groups.length === 0 ? (
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-6 text-center text-slate-600">
                    No appointments found for this range.
                  </div>
                ) : null}
                <div
                  id="appointment-status-details"
                  className={getSectionClassName("appointment-status-details")}
                >
                  {groups.map((group) => (
                    <details
                      key={group.group_key}
                      className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
                      open
                    >
                      <summary className="cursor-pointer select-none px-4 py-3 flex flex-wrap items-center justify-between gap-3 bg-slate-50">
                        <div className="text-base font-semibold text-slate-800">
                          {group.group_label}
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs text-slate-600">
                          {STATUS_OPTIONS.map((status) => (
                            <span
                              key={`${group.group_key}-${status.value}`}
                              className="rounded-full border border-slate-200 bg-white px-2 py-1"
                            >
                              {status.label}: {group.counts?.[status.value] ?? 0}
                            </span>
                          ))}
                        </div>
                      </summary>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-slate-50 text-slate-600">
                            <tr>
                              <th className="px-4 py-3 text-left font-semibold">Start time</th>
                              <th className="px-4 py-3 text-left font-semibold">Status</th>
                              <th className="px-4 py-3 text-left font-semibold">Employee</th>
                              <th className="px-4 py-3 text-left font-semibold">Client</th>
                              <th className="px-4 py-3 text-left font-semibold">Service summary</th>
                              <th className="px-4 py-3 text-left font-semibold">Duration</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(group.appointments || []).map((appt) => (
                              <tr
                                key={appt.appointment_id}
                                className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                                onClick={() => handleRowClick(appt)}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") handleRowClick(appt);
                                }}
                                tabIndex={0}
                                role="button"
                              >
                                <td className="px-4 py-3 text-slate-700">
                                  {formatInTimeZone(
                                    appt.start_at,
                                    reportTimezone,
                                    {
                                      year: "numeric",
                                      month: "short",
                                      day: "2-digit",
                                      hour: "numeric",
                                      minute: "2-digit",
                                    },
                                    "en-US"
                                  )}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {formatStatusLabel(appt.status)}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {appt.employee_name || "--"}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {appt.client_name || "--"}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {appt.service_summary || "--"}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {appt.duration_minutes ? `${appt.duration_minutes} min` : "--"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            ) : null}

            <div
              id="appointment-status-export"
              className={`rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600 ${getSectionClassName(
                "appointment-status-export"
              )}`}
            >
              Export is not available on this report.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
